import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AnimatedDemo.css';

const DEMO_TEXT = "Dr Smith specializes in anxiety and depression treatment. We guarantee you'll feel significantly better after just 6 sessions. Here's what one of our clients said: 'Dr Smith cured my anxiety completely!' - Sarah M.";

const DEMO_RESULTS = [
  {
    risk: 'high',
    label: 'High Risk',
    title: 'SPECIALIST/SPECIALIZES (CRITICAL - S.4.1.4.B)',
    problem: '"Dr Smith specializes in anxiety and depression treatment."',
    explanation: "The use of the term 'specializes' is misleading as Dr Smith does not hold specialist registration, violating guideline 4.1.4.b.",
    suggestion: 'Dr Smith has substantial experience in treating anxiety and depression.'
  },
  {
    risk: 'medium',
    label: 'Medium Risk',
    title: 'UNREASONABLE EXPECTATION (S.133(1)(D))',
    problem: '"We guarantee you\'ll feel significantly better after just 6 sessions."',
    explanation: 'This statement creates an unreasonable expectation of beneficial treatment, violating guideline 133(1)(d).',
    suggestion: 'We aim to help you feel better through our treatment approach.'
  },
  {
    risk: 'high',
    label: 'High Risk',
    title: 'TESTIMONIALS (S.133(1)(C))',
    problem: '"\'Dr Smith cured my anxiety completely!\' - Sarah M."',
    explanation: 'This statement is a testimonial about clinical outcomes, which is not allowed under guideline 133(1)(c).',
    suggestion: 'Remove this testimonial entirely. Clinical testimonials cannot be reworded to be compliant - they must be removed.'
  }
];

// Phases of the animation
const PHASE = {
  IDLE: 'idle',
  CURSOR_MOVE: 'cursorMove',
  RIGHT_CLICK: 'rightClick',
  CONTEXT_MENU: 'contextMenu',
  CURSOR_TO_PASTE: 'cursorToPaste',
  PASTE: 'paste',
  CURSOR_TO_SEND: 'cursorToSend',
  CLICKING: 'clicking',
  MESSAGE_SENT: 'messageSent',
  LOADING: 'loading',
  RESULTS: 'results',
  SCROLLING: 'scrolling',
  PAUSE: 'pause',
  FADE_OUT: 'fadeOut'
};

function AnimatedDemo() {
  const [phase, setPhase] = useState(PHASE.IDLE);
  const [pastedText, setPastedText] = useState('');
  const [visibleResults, setVisibleResults] = useState([]);
  const [hoveredMenuItem, setHoveredMenuItem] = useState(null); // Track which menu item cursor is over
  const [opacity, setOpacity] = useState(1);
  const [chatScrollOffset, setChatScrollOffset] = useState(0);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [cursorPos, setCursorPos] = useState({ x: 350, y: 50 }); // Start off-screen top
  const [showMouseCursor, setShowMouseCursor] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 }); // Fixed position for context menu

  const timeoutRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const scrollAnimationRef = useRef(null);
  const menuPosRef = useRef({ x: 300, y: 340 }); // Store menu position for cursor calculations

  // Cleanup function
  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }
  }, []);

  // Reset state for new loop
  const resetDemo = useCallback(() => {
    cleanup();
    setPastedText('');
    setVisibleResults([]);
    setOpacity(1);
    setChatScrollOffset(0);
    setShowContextMenu(false);
    setCursorPos({ x: 350, y: 50 });
    setShowMouseCursor(false);
    setContextMenuPos({ x: 0, y: 0 });
    setHoveredMenuItem(null);
    setPhase(PHASE.IDLE);
  }, [cleanup]);


  // Main animation state machine
  useEffect(() => {
    cleanup();

    switch (phase) {
      case PHASE.IDLE:
        // Start cursor off-screen to the right, then swipe in
        setCursorPos({ x: 650, y: 380 });
        timeoutRef.current = setTimeout(() => {
          setShowMouseCursor(true);
          setPhase(PHASE.CURSOR_MOVE);
        }, 1500);
        break;

      case PHASE.CURSOR_MOVE:
        // Animate cursor sweeping in from right to center of input area
        setCursorPos({ x: 300, y: 380 });
        timeoutRef.current = setTimeout(() => {
          setPhase(PHASE.RIGHT_CLICK);
        }, 700);
        break;

      case PHASE.RIGHT_CLICK:
        // Show context menu at cursor position (save the position)
        menuPosRef.current = { x: 300, y: 380 };
        setContextMenuPos({ x: 300, y: 380 });
        setShowContextMenu(true);
        timeoutRef.current = setTimeout(() => {
          setPhase(PHASE.CONTEXT_MENU);
        }, 400);
        break;

      case PHASE.CONTEXT_MENU:
        // Move cursor down to Copy first
        setCursorPos({ x: menuPosRef.current.x + 40, y: menuPosRef.current.y + 18 });
        setHoveredMenuItem('copy');

        // Then move to Paste
        timeoutRef.current = setTimeout(() => {
          setPhase(PHASE.CURSOR_TO_PASTE);
        }, 300);
        break;

      case PHASE.CURSOR_TO_PASTE:
        // Move cursor to Paste option
        setCursorPos({ x: menuPosRef.current.x + 40, y: menuPosRef.current.y + 52 });
        setHoveredMenuItem('paste');
        timeoutRef.current = setTimeout(() => {
          setPhase(PHASE.PASTE);
        }, 400);
        break;

      case PHASE.PASTE:
        // Hide context menu and paste text instantly
        setShowContextMenu(false);
        setHoveredMenuItem(null);
        setPastedText(DEMO_TEXT);

        // Move cursor to send button after a brief pause
        timeoutRef.current = setTimeout(() => {
          setPhase(PHASE.CURSOR_TO_SEND);
        }, 500);
        break;

      case PHASE.CURSOR_TO_SEND:
        // Animate cursor to send button (center of the teal send button)
        setCursorPos({ x: 510, y: 390 });
        timeoutRef.current = setTimeout(() => {
          setPhase(PHASE.CLICKING);
        }, 600);
        break;

      case PHASE.CLICKING:
        // Brief click animation, then hide cursor
        timeoutRef.current = setTimeout(() => {
          setShowMouseCursor(false);
          setPhase(PHASE.MESSAGE_SENT);
        }, 300);
        break;

      case PHASE.MESSAGE_SENT:
        // Show message briefly then start loading
        timeoutRef.current = setTimeout(() => {
          setPhase(PHASE.LOADING);
        }, 500);
        break;

      case PHASE.LOADING:
        // Loading animation for 2 seconds
        timeoutRef.current = setTimeout(() => {
          setPhase(PHASE.RESULTS);
        }, 2000);
        break;

      case PHASE.RESULTS:
        // Show all results immediately
        setVisibleResults([0, 1, 2]);

        // Start scrolling after a brief pause
        timeoutRef.current = setTimeout(() => {
          setPhase(PHASE.SCROLLING);
        }, 1500);
        break;

      case PHASE.SCROLLING:
        // Smooth continuous scroll through all results
        const startTime = Date.now();
        const duration = 8000; // 8 seconds for full scroll (slower for readability)
        const maxScroll = 520; // Total scroll distance to show all results

        const animateScroll = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // Ease-in-out for smooth feel
          const easeProgress = progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;

          setChatScrollOffset(easeProgress * maxScroll);

          if (progress < 1) {
            scrollAnimationRef.current = requestAnimationFrame(animateScroll);
          } else {
            // Scrolling complete, pause then fade
            timeoutRef.current = setTimeout(() => {
              setPhase(PHASE.PAUSE);
            }, 2000);
          }
        };

        scrollAnimationRef.current = requestAnimationFrame(animateScroll);
        break;

      case PHASE.PAUSE:
        // Pause complete, start fade out
        timeoutRef.current = setTimeout(() => {
          setPhase(PHASE.FADE_OUT);
        }, 500);
        break;

      case PHASE.FADE_OUT:
        setOpacity(0);
        timeoutRef.current = setTimeout(() => {
          resetDemo();
        }, 800);
        break;

      default:
        break;
    }

    return cleanup;
  }, [phase, cleanup, resetDemo]);

  const showInput = [PHASE.IDLE, PHASE.CURSOR_MOVE, PHASE.RIGHT_CLICK, PHASE.CONTEXT_MENU, PHASE.CURSOR_TO_PASTE, PHASE.PASTE, PHASE.CURSOR_TO_SEND, PHASE.CLICKING].includes(phase);
  const showUserMessage = [PHASE.MESSAGE_SENT, PHASE.LOADING, PHASE.RESULTS, PHASE.SCROLLING, PHASE.PAUSE, PHASE.FADE_OUT].includes(phase);
  const showLoading = phase === PHASE.LOADING;
  const showResults = [PHASE.RESULTS, PHASE.SCROLLING, PHASE.PAUSE, PHASE.FADE_OUT].includes(phase);

  return (
    <div className="animated-demo" style={{ opacity }}>
      <div className="demo-window" style={{ position: 'relative' }}>
        {/* Mouse Cursor */}
        {showMouseCursor && (
          <div
            className="demo-mouse-cursor"
            style={{
              left: cursorPos.x,
              top: cursorPos.y
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="black" stroke="white" strokeWidth="1">
              <path d="M5.5 3.21V20.8l5.71-5.71h7.29L5.5 3.21z" />
            </svg>
          </div>
        )}
        {/* Window header */}
        <div className="demo-header">
          <div className="demo-traffic-lights">
            <span className="traffic-light red"></span>
            <span className="traffic-light yellow"></span>
            <span className="traffic-light green"></span>
          </div>
          <span className="demo-title">Mindmark</span>
          <div className="demo-header-spacer"></div>
        </div>

        {/* Chat area */}
        <div className="demo-chat">
          <div
            className="demo-chat-scroll"
            style={{ transform: `translateY(-${chatScrollOffset}px)` }}
          >
            {/* Empty state */}
            {!showUserMessage && !showLoading && !showResults && (
              <div className="demo-empty-state">
                <p>Paste text, enter a URL, or upload a document to check for AHPRA compliance.</p>
              </div>
            )}

            {/* User message */}
            {showUserMessage && (
              <div className="demo-message demo-user-message">
                <div className="demo-message-label">Checking text:</div>
                <div className="demo-message-content">"{DEMO_TEXT.substring(0, 80)}..."</div>
              </div>
            )}

            {/* Loading state */}
            {showLoading && (
              <div className="demo-loading">
                <div className="demo-spinner"></div>
                <span>Analyzing content...</span>
              </div>
            )}

            {/* Results */}
            {showResults && (
              <div className="demo-results">
                <div className="demo-results-header">
                  <h4>Compliance Check Results</h4>
                  <p>Found {DEMO_RESULTS.length} potential issue(s):</p>
                </div>

                {DEMO_RESULTS.map((result, index) => (
                  <div
                    key={index}
                    className={`demo-issue demo-issue-${result.risk} ${visibleResults.includes(index) ? 'visible' : ''}`}
                  >
                    <div className="demo-issue-header">
                      <span className={`demo-risk-badge demo-risk-${result.risk}`}>
                        {result.label}
                      </span>
                      <span className="demo-issue-title">{result.title}</span>
                    </div>
                    <p className="demo-issue-problem">
                      <strong>Problem:</strong> {result.problem}
                    </p>
                    <p className="demo-issue-explanation">{result.explanation}</p>
                    <div className="demo-issue-suggestion">
                      <strong>Suggestion:</strong> {result.suggestion}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Input area */}
        <div className="demo-input-area">
          <div className={`demo-input-wrapper ${phase === PHASE.CLICKING ? 'clicking' : ''}`}>
            <button className="demo-add-btn">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>
            <div className="demo-textarea">
              {showInput && pastedText ? (
                <span className="demo-pasted-text">{pastedText}</span>
              ) : (
                <span className="demo-placeholder">Paste text or a URL to check for AHPRA compliance...</span>
              )}
            </div>
            <button className={`demo-send-btn ${phase === PHASE.CLICKING ? 'active' : ''}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>

          {/* Context Menu */}
          {showContextMenu && (
            <div
              className="demo-context-menu"
              style={{
                left: contextMenuPos.x,
                top: contextMenuPos.y
              }}
            >
              <div className={`demo-context-item ${hoveredMenuItem === 'copy' ? 'highlighted' : ''}`}>Copy</div>
              <div className={`demo-context-item ${hoveredMenuItem === 'paste' ? 'highlighted' : ''}`}>Paste</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AnimatedDemo;
